import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faHome,
  faBox,
  faTruck,
  faShoppingCart,
  faSignOutAlt,
  faSignInAlt,
  faTachometerAlt,
  faList,
  faBars,
  faTimes,
  faUserPlus,
  faChevronDown,
  faCog,
  faInfoCircle,
  faEnvelope,
  faShieldAlt,
  faClipboardList,
  faMoneyBillWave,
  faUserCircle,
  faStore,
  faTractor,
  faCrown,
} from "@fortawesome/free-solid-svg-icons";
import { useAuth } from "../../context/AuthContext";
import { useCart } from "../../context/CartContext";
import { useCurrency } from "../../context/CurrencyContext";
import CurrencySelector from "../UI/CurrencySelector";
import logoImage from "../../assets/Logo.png";
import "./navbar.scss";

// ============================================
// BRAND CONSTANTS
// ============================================
const BRAND = {
  primary: "#006B2E",
  secondary: "#D9A321",
  goldHover: "#B8860B",
  forest: "#014421",
  charcoal: "#1F1F1F",
  slate: "#5A5A5A",
  white: "#FFFFFF",
  lightBg: "#F8F8F8",
};

// ============================================
// NAVIGATION LINKS
// ============================================
const NAV_LINKS = [
  { to: "/", label: "Home", icon: faHome },
  { to: "/about", label: "About", icon: faInfoCircle },
  { to: "/products", label: "Products", icon: faBox },
  { to: "/preorder", label: "Pre-Order", icon: faClipboardList },
  { to: "/track-order", label: "Track Order", icon: faTruck },
  { to: "/contact", label: "Contact", icon: faEnvelope },
];

// ============================================
// MAIN NAVBAR COMPONENT
// ============================================
const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const mobileMenuRef = useRef(null);
  const { user, logout, isAdmin, isSuperAdmin, isFarmer, isSupplier } = useAuth();
  const { getCartCount } = useCart();
  const { getSymbol } = useCurrency();
  const navigate = useNavigate();

  // ============================================
  // EFFECTS
  // ============================================
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.body.style.overflow = "auto";
    };
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // ============================================
  // HANDLERS
  // ============================================
  const handleLogout = () => {
    logout();
    navigate("/");
    setIsOpen(false);
    setDropdownOpen(false);
  };

  const handleNavClick = () => {
    setIsOpen(false);
    setDropdownOpen(false);
  };

  const cartCount = getCartCount();

  // ============================================
  // USER HELPERS
  // ============================================
  const getUserInitials = () => {
    if (!user?.name) return "U";
    return user.name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const getUserRoleLabel = () => {
    if (isSuperAdmin?.()) return "👑 Super Admin";
    if (isAdmin?.()) return "🛡️ Administrator";
    if (isFarmer?.()) return "🌾 Farmer";
    if (isSupplier?.()) return "🏢 Supplier";
    return "🛒 Customer";
  };

  const getUserRoleIcon = () => {
    if (isSuperAdmin?.() || isAdmin?.()) return faCrown;
    if (isFarmer?.()) return faTractor;
    if (isSupplier?.()) return faStore;
    return faUserCircle;
  };

  // ============================================
  // RENDER
  // ============================================
  return (
    <nav className={`navbar ${scrolled ? "scrolled" : ""}`}>
      <div className="nav-container">
        {/* Logo */}
        <Link to="/" className="logo" onClick={handleNavClick}>
          {logoImage && (
            <img src={logoImage} alt="Kenali Enterprise" className="logo-img" />
          )}
          <span className="logo-text">
            Kenali <span className="logo-highlight">Enterprise</span>
          </span>
          {!scrolled && (
            <span className="currency-indicator">
              <FontAwesomeIcon icon={faMoneyBillWave} />
              {getSymbol()}
            </span>
          )}
        </Link>

        {/* Mobile Toggle */}
        <button
          className="mobile-toggle"
          onClick={() => setIsOpen(!isOpen)}
          aria-label="Menu"
          aria-expanded={isOpen}
        >
          <FontAwesomeIcon icon={isOpen ? faTimes : faBars} />
        </button>

        {/* Navigation Links */}
        <AnimatePresence>
          {(isOpen || !isOpen) && (
            <div className={`nav-links ${isOpen ? "active" : ""}`} ref={mobileMenuRef}>
              {/* Main Nav Links */}
              {NAV_LINKS.map((link, index) => (
                <motion.div
                  key={link.to}
                  initial={isOpen ? { opacity: 0, x: -20 } : false}
                  animate={isOpen ? { opacity: 1, x: 0 } : false}
                  transition={{ duration: 0.3, delay: index * 0.05 }}
                >
                  <Link
                    to={link.to}
                    onClick={handleNavClick}
                    className="nav-link"
                  >
                    <FontAwesomeIcon icon={link.icon} /> {link.label}
                  </Link>
                </motion.div>
              ))}

              {/* Currency Selector */}
              <motion.div
                initial={isOpen ? { opacity: 0, x: -20 } : false}
                animate={isOpen ? { opacity: 1, x: 0 } : false}
                transition={{ duration: 0.3, delay: 0.1 }}
              >
                <CurrencySelector />
              </motion.div>

              {/* Cart Link */}
              <motion.div
                initial={isOpen ? { opacity: 0, x: -20 } : false}
                animate={isOpen ? { opacity: 1, x: 0 } : false}
                transition={{ duration: 0.3, delay: 0.15 }}
              >
                <Link to="/cart" onClick={handleNavClick} className="cart-link">
                  <FontAwesomeIcon icon={faShoppingCart} />
                  {cartCount > 0 && (
                    <motion.span
                      className="cart-badge"
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ type: "spring", stiffness: 500, damping: 30 }}
                    >
                      {cartCount}
                    </motion.span>
                  )}
                </Link>
              </motion.div>

              {/* User Dropdown / Auth Links */}
              {user ? (
                <motion.div
                  className="user-dropdown"
                  ref={dropdownRef}
                  initial={isOpen ? { opacity: 0, x: -20 } : false}
                  animate={isOpen ? { opacity: 1, x: 0 } : false}
                  transition={{ duration: 0.3, delay: 0.2 }}
                >
                  <button
                    className="dropdown-trigger"
                    onClick={() => setDropdownOpen(!dropdownOpen)}
                    aria-label="User menu"
                  >
                    <div className="user-avatar">
                      {user.avatar ? (
                        <img src={user.avatar} alt={user.name} />
                      ) : (
                        <span>{getUserInitials()}</span>
                      )}
                    </div>
                    <span className="user-name">
                      {user.name?.split(" ")[0] || "Account"}
                    </span>
                    <FontAwesomeIcon
                      icon={faChevronDown}
                      className={`dropdown-arrow ${dropdownOpen ? "open" : ""}`}
                    />
                  </button>

                  <AnimatePresence>
                    {dropdownOpen && (
                      <motion.div
                        className="dropdown-menu"
                        initial={{ opacity: 0, y: -10, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -10, scale: 0.95 }}
                        transition={{ duration: 0.2 }}
                      >
                        <div className="dropdown-header">
                          <div className="dropdown-user-info">
                            <div className="dropdown-user-avatar">
                              {user.avatar ? (
                                <img src={user.avatar} alt={user.name} />
                              ) : (
                                <span>{getUserInitials()}</span>
                              )}
                            </div>
                            <div>
                              <div className="dropdown-user-name">
                                {user.name}
                              </div>
                              <div className="dropdown-user-email">
                                {user.email}
                              </div>
                              <div className="dropdown-user-role">
                                <FontAwesomeIcon icon={getUserRoleIcon()} />
                                {getUserRoleLabel()}
                              </div>
                            </div>
                          </div>
                        </div>

                        <div className="dropdown-divider" />

                        <Link
                          to="/dashboard"
                          className="dropdown-item"
                          onClick={handleNavClick}
                        >
                          <FontAwesomeIcon icon={faTachometerAlt} /> Dashboard
                        </Link>

                        <Link
                          to="/orders"
                          className="dropdown-item"
                          onClick={handleNavClick}
                        >
                          <FontAwesomeIcon icon={faList} /> My Orders
                        </Link>

                        <Link
                          to="/profile"
                          className="dropdown-item"
                          onClick={handleNavClick}
                        >
                          <FontAwesomeIcon icon={faCog} /> Account Settings
                        </Link>

                        {(isAdmin?.() || isSuperAdmin?.()) && (
                          <>
                            <div className="dropdown-divider" />
                            <Link
                              to="/admin"
                              className="dropdown-item admin"
                              onClick={handleNavClick}
                            >
                              <FontAwesomeIcon icon={faShieldAlt} /> Admin Panel
                            </Link>
                          </>
                        )}

                        <div className="dropdown-divider" />

                        <button
                          className="dropdown-item logout"
                          onClick={handleLogout}
                        >
                          <FontAwesomeIcon icon={faSignOutAlt} /> Logout
                        </button>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              ) : (
                <>
                  <motion.div
                    initial={isOpen ? { opacity: 0, x: -20 } : false}
                    animate={isOpen ? { opacity: 1, x: 0 } : false}
                    transition={{ duration: 0.3, delay: 0.25 }}
                  >
                    <Link
                      to="/login"
                      onClick={handleNavClick}
                      className="nav-link login-link"
                    >
                      <FontAwesomeIcon icon={faSignInAlt} /> Login
                    </Link>
                  </motion.div>
                  <motion.div
                    initial={isOpen ? { opacity: 0, x: -20 } : false}
                    animate={isOpen ? { opacity: 1, x: 0 } : false}
                    transition={{ duration: 0.3, delay: 0.3 }}
                  >
                    <Link
                      to="/register"
                      onClick={handleNavClick}
                      className="register-link"
                    >
                      <FontAwesomeIcon icon={faUserPlus} /> Register
                    </Link>
                  </motion.div>
                </>
              )}
            </div>
          )}
        </AnimatePresence>
      </div>
    </nav>
  );
};

export default Navbar;